import { isEqual } from '@travelata-libs/utils';
import {
  getDestinationResortIds,
  isDirectFlightAvailable,
  withDestinationResorts,
  type SearchCriteria,
} from '@/entities/searchCriteria';
import { EMIRATES_FILTER_GROUP_ID, isEmiratesDirection } from './emiratesFilter';
import { SERP_FILTER_GROUPS } from '../config/filters';
import type {
  SearchCriteriaFilters,
  SearchCriteriaFlagGroupId,
  SearchFilterGroupId,
  SearchFiltersSource,
  SearchListFilterGroupId,
  SerpCriteriaFilterGroup,
  SerpFilterGroup,
  SerpFilterGroupId,
} from '../model/types';

export const DIRECT_FLIGHT_FILTER_GROUP_ID: SearchCriteriaFlagGroupId = 'directFlight';

export function isSearchFilterGroup(group: SerpFilterGroup): group is SerpCriteriaFilterGroup {
  return group.search === true;
}

export function isSearchFilterGroupId(groupId: SerpFilterGroupId): groupId is SearchFilterGroupId {
  return SERP_FILTER_GROUPS.some((group) => isSearchFilterGroup(group) && group.id === groupId);
}

function getSearchFilterGroupIds(): SearchFilterGroupId[] {
  return SERP_FILTER_GROUPS.flatMap((group) => (isSearchFilterGroup(group) ? [group.id] : []));
}

function isSearchFlagGroupId(groupId: SearchFilterGroupId): groupId is SearchCriteriaFlagGroupId {
  return groupId === DIRECT_FLIGHT_FILTER_GROUP_ID || groupId === EMIRATES_FILTER_GROUP_ID;
}

export function createEmptySearchCriteriaFilters(): SearchCriteriaFilters {
  return { meals: [], hotelCategories: [], resorts: [] };
}

export function getSearchCriteriaFilters(criteria: SearchFiltersSource): SearchCriteriaFilters {
  return {
    meals: criteria.meals ?? [],
    hotelCategories: criteria.hotelCategories ?? [],
    resorts: getDestinationResortIds(criteria.destination),
    ...(criteria.directFlight && isDirectFlightAvailable(criteria) && { directFlight: true }),
    ...(criteria.block && isEmiratesDirection(criteria) && { block: true }),
  };
}

function getSelectedIds(criteriaFilters: SearchCriteriaFilters, groupId: SearchListFilterGroupId): number[] {
  return criteriaFilters[groupId] ?? [];
}

function withoutId(ids: readonly number[], id: number): number[] {
  return ids.filter((selectedId) => selectedId !== id);
}

function sortIds(ids: readonly number[]): number[] {
  return [...ids].sort((first, second) => first - second);
}

export function isSearchFilterOptionSelected(
  criteriaFilters: SearchCriteriaFilters,
  groupId: SearchFilterGroupId,
  key: string,
): boolean {
  if (isSearchFlagGroupId(groupId)) return Boolean(criteriaFilters[groupId]);

  return getSelectedIds(criteriaFilters, groupId).includes(Number(key));
}

export function toggleSearchFilter(
  criteriaFilters: SearchCriteriaFilters,
  groupId: SearchFilterGroupId,
  key: string,
): SearchCriteriaFilters {
  if (isSearchFlagGroupId(groupId)) {
    return { ...criteriaFilters, [groupId]: criteriaFilters[groupId] ? undefined : true };
  }

  const id = Number(key);
  const selectedIds = getSelectedIds(criteriaFilters, groupId);

  return {
    ...criteriaFilters,
    [groupId]: selectedIds.includes(id) ? withoutId(selectedIds, id) : [...selectedIds, id],
  };
}

export function clearSearchFilterGroup(
  criteriaFilters: SearchCriteriaFilters,
  groupId: SearchFilterGroupId,
): SearchCriteriaFilters {
  return { ...criteriaFilters, [groupId]: createEmptySearchCriteriaFilters()[groupId] };
}

export function removeSearchFilter(
  criteriaFilters: SearchCriteriaFilters,
  groupId: SearchFilterGroupId,
  key: string,
): SearchCriteriaFilters {
  if (isSearchFlagGroupId(groupId)) return clearSearchFilterGroup(criteriaFilters, groupId);

  return { ...criteriaFilters, [groupId]: withoutId(getSelectedIds(criteriaFilters, groupId), Number(key)) };
}

function isSearchFilterGroupEqual(
  first: SearchCriteriaFilters,
  second: SearchCriteriaFilters,
  groupId: SearchFilterGroupId,
): boolean {
  if (isSearchFlagGroupId(groupId)) return Boolean(first[groupId]) === Boolean(second[groupId]);

  return isEqual(sortIds(getSelectedIds(first, groupId)), sortIds(getSelectedIds(second, groupId)));
}

export function isSearchCriteriaFiltersChanged(
  criteria: SearchFiltersSource,
  criteriaFilters: SearchCriteriaFilters,
): boolean {
  const appliedFilters = getSearchCriteriaFilters(criteria);

  return getSearchFilterGroupIds().some(
    (groupId) => !isSearchFilterGroupEqual(appliedFilters, criteriaFilters, groupId),
  );
}

export function isSearchFilterGroupChanged(
  criteria: SearchFiltersSource,
  criteriaFilters: SearchCriteriaFilters,
  groupId: SearchFilterGroupId,
): boolean {
  return !isSearchFilterGroupEqual(getSearchCriteriaFilters(criteria), criteriaFilters, groupId);
}

export function isSearchFilterGroupSelected(
  criteriaFilters: SearchCriteriaFilters,
  groupId: SearchFilterGroupId,
): boolean {
  return !isSearchFilterGroupEqual(createEmptySearchCriteriaFilters(), criteriaFilters, groupId);
}

export function isSearchCriteriaFiltersSelected(criteriaFilters: SearchCriteriaFilters): boolean {
  return getSearchFilterGroupIds().some((groupId) => isSearchFilterGroupSelected(criteriaFilters, groupId));
}

export function setSearchCriteriaFilters(
  criteria: SearchCriteria,
  criteriaFilters: SearchCriteriaFilters,
): SearchCriteria {
  return {
    ...criteria,
    meals: criteriaFilters.meals ?? [],
    hotelCategories: criteriaFilters.hotelCategories ?? [],
    directFlight: criteriaFilters.directFlight,
    block: criteriaFilters.block,
    destination: criteriaFilters.resorts
      ? withDestinationResorts(criteria.destination, criteriaFilters.resorts)
      : criteria.destination,
  };
}
